// org_admin email follow-up monitor — KPI cards + tabbed rep/vendor table.

import React, { useState } from "react";
import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { Badge } from "../atoms";
import { useEmailFollowUpAdminMonitor } from "../hooks/useFollowUps";

const TABS = [
  { id: "overdue", label: "Overdue", tone: "danger" },
  { id: "due_today", label: "Due today", tone: "warning" },
  { id: "pending", label: "Pending", tone: "neutral" },
  { id: "complete", label: "Complete", tone: "success" },
];

function formatErr(err) {
  return err?.response?.data?.error || err?.message || "Request failed";
}

function KpiCard({ label, value, tone }) {
  const styles = {
    danger: { bg: "var(--crm-danger-soft, #fef2f2)", color: "var(--crm-danger)" },
    warning: { bg: "var(--crm-warning-soft, #fffbeb)", color: "var(--crm-warning, #B45309)" },
    neutral: { bg: "var(--color-bg-muted)", color: "var(--color-text-primary)" },
    primary: { bg: "var(--crm-primary-soft)", color: "var(--crm-primary)" },
  };
  const s = styles[tone] || styles.neutral;
  return (
    <div style={{
      flex: 1, minWidth: 120, padding: "14px 16px", borderRadius: 8,
      background: s.bg, border: "1px solid var(--color-border-subtle)",
    }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)" }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 600, marginTop: 6, color: s.color, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ row, tab }) {
  if (tab === "complete") {
    return <Badge tone="success">Vendor replied</Badge>;
  }
  if (tab === "pending") {
    return <Badge tone="primary">On track</Badge>;
  }
  if (tab === "due_today") {
    return <Badge tone="warning">Due today</Badge>;
  }
  const tone = row.status === "escalated" ? "danger" : "danger";
  return <Badge tone={tone}>{row.statusLabel}</Badge>;
}

export default function EmailFollowUpAdminMonitor({ compact = false }) {
  const [tab, setTab] = useState("overdue");
  const { data, isLoading, isError, error, refetch, isFetching } = useEmailFollowUpAdminMonitor({ tab });

  const counts = data?.counts || { overdue: 0, due_today: 0, pending: 0, complete: 0 };
  const items = data?.items || [];
  const avgTat = data?.avgTatDays != null ? `${data.avgTatDays}d` : "—";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {!compact && (
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--crm-primary)" }}>
              Email follow-ups
            </div>
            <h2 style={{ margin: "4px 0 0", fontSize: 18, fontWeight: 600 }}>Team monitor</h2>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            style={{
              height: 32, padding: "0 12px", borderRadius: 8,
              border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-surface)",
              cursor: "pointer", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", padding: compact ? 0 : "16px 20px" }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          <KpiCard label="Overdue" value={counts.overdue} tone="danger" />
          <KpiCard label="Due today" value={counts.due_today} tone="warning" />
          <KpiCard label="Pending" value={counts.pending} tone="neutral" />
          <KpiCard label="Avg TAT" value={avgTat} tone="primary" />
        </div>

        <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--color-border-subtle)", marginBottom: 0 }}>
          {TABS.map((t) => {
            const active = tab === t.id;
            const count = counts[t.id] ?? 0;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                style={{
                  padding: "10px 14px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  color: active ? "var(--color-text-primary)" : "var(--color-text-secondary)",
                  borderBottom: active ? "2px solid var(--crm-danger)" : "2px solid transparent",
                  marginBottom: -1,
                }}
              >
                {t.label} ({count})
              </button>
            );
          })}
        </div>

        {isLoading && (
          <div style={{ padding: 40, display: "flex", justifyContent: "center", gap: 8, color: "var(--color-text-secondary)" }}>
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}
        {isError && (
          <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: "var(--crm-danger-soft)", color: "var(--crm-danger)", fontSize: 13 }}>
            <AlertCircle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {formatErr(error)}
          </div>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
            No threads in this bucket.
          </div>
        )}
        {!isLoading && !isError && items.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)" }}>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid var(--color-border-subtle)" }}>Rep</th>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid var(--color-border-subtle)" }}>Vendor / lead</th>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid var(--color-border-subtle)" }}>Day 0</th>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid var(--color-border-subtle)" }}>Touches</th>
                  <th style={{ padding: "10px 12px", borderBottom: "1px solid var(--color-border-subtle)" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row._id} style={{ borderBottom: "1px solid var(--color-border-subtle)" }}>
                    <td style={{ padding: "12px", fontWeight: 500 }}>{row.rep}</td>
                    <td style={{ padding: "12px" }}>
                      <div style={{ fontWeight: 600 }}>{row.vendorLead}</div>
                      {!row.leadInCrm && (
                        <span style={{
                          display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 600,
                          padding: "2px 6px", borderRadius: 4, background: "var(--color-bg-muted)",
                          color: "var(--color-text-secondary)",
                        }}
                        >
                          not in CRM
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "12px", color: "var(--color-text-secondary)" }}>{row.day0}</td>
                    <td style={{ padding: "12px", fontVariantNumeric: "tabular-nums" }}>{row.touches}</td>
                    <td style={{ padding: "12px" }}>
                      <StatusBadge row={row} tab={tab} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
